import { useCallback, useState } from 'react'
import { sharedAws } from '@/service/bucketS3/Storage'
import { UploadedEvidenceFile } from '../types'

const cleanFileName = (fileName: string) => fileName.replace(/\s+/g, '')

const buildKey = (uploadName: string, fileName: string) =>
  `${uploadName}/${cleanFileName(fileName)}`

export const usePlanEvidenceFiles = () => {
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set())
  const [errors, setErrors] = useState<Record<string, string>>({})

  const uploadFile = useCallback(
    async (
      file: File,
      evidenceTypeId: number,
      uploadName: string
    ): Promise<UploadedEvidenceFile | null> => {
      const fileName = cleanFileName(file.name)
      const key = buildKey(uploadName, fileName)

      setUploadingFiles((prev) => new Set(prev).add(key))
      setErrors((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })

      try {
        const url = await sharedAws.uploadAndGetUrl({ file, name: uploadName })
        return {
          name: fileName,
          url,
          key,
          evidenceTypeId,
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Error uploading file'
        setErrors((prev) => ({ ...prev, [key]: message }))
        return null
      } finally {
        setUploadingFiles((prev) => {
          const next = new Set(prev)
          next.delete(key)
          return next
        })
      }
    },
    []
  )

  const uploadFiles = useCallback(
    async (
      files: File[],
      evidenceTypeId: number,
      uploadName: string
    ): Promise<UploadedEvidenceFile[]> => {
      const results = await Promise.all(
        files.map((file) => uploadFile(file, evidenceTypeId, uploadName))
      )
      return results.filter((item): item is UploadedEvidenceFile => item !== null)
    },
    [uploadFile]
  )

  const deleteFile = useCallback(async (key: string): Promise<boolean> => {
    try {
      await sharedAws.delete({ name: key })
      return true
    } catch (error) {
      console.error('Error deleting file from S3:', error)
      return false
    }
  }, [])

  const clearError = useCallback((key: string) => {
    setErrors((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [])

  return {
    uploadFile,
    uploadFiles,
    deleteFile,
    uploadingFiles,
    errors,
    clearError,
  }
}
